import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import TimerIcon from '@mui/icons-material/Timer';
import { useNavigate } from 'react-router-dom';

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  borderBottom: '1px solid #E0E0E0',
  zIndex: 1000,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  padding: '12px 20px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
  },
}));

const TimerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#333',
  fontWeight: 'medium',
  '& .MuiSvgIcon-root': {
    color: '#996CFE',
    marginRight: '8px',
  },
}));

const TimerNumber = styled('span')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.15em',
  color: '#2D3748',
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#37965E',
  color: 'white',
  padding: '8px 24px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#2A7349',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E0E0E0',
    color: '#9E9E9E',
  },
}));

const ReviewButton = styled(Button)(({ theme }) => ({
  color: '#996CFE',
  backgroundColor: 'transparent',
  border: '1px solid #996CFE',
  padding: '6px 16px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(153, 108, 254, 0.08)',
  },
}));

const IELTSHeader = ({
  timerText,
  middleContent = null,
  showReview = true,
  showSaveExit = false,
  showSubmit = true,
  isSubmitting = false,
  apiResult = null,
  groupId = null,
  onReview,
  onSaveExit,
  onSubmit,
  onSubmitNavigate,
  submitButtonText,
  submitButtonDisabled = false,
}) => {
  const navigate = useNavigate();

  const handleSubmitClick = () => {
    if (apiResult && !groupId && onSubmitNavigate) {
      navigate(onSubmitNavigate);
    } else if (onSubmit) {
      onSubmit();
    }
  };

  const getSubmitButtonText = () => {
    if (submitButtonText) return submitButtonText;
    if (apiResult && !groupId) return 'Results';
    if (isSubmitting) return 'Submitting...';
    return 'Submit';
  };

  const renderTimerText = (text) => {
    if (text === 'Unlimited') return text;
    
    // Match numbers (including negative) in the text
    const parts = text.split(/(\d+)/);
    return parts.map((part, index) => {
      if (/^\d+$/.test(part)) {
        return <TimerNumber key={index}>{part}</TimerNumber>;
      }
      return part;
    });
  };

  return (
    <Header>
      {/* Left: Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <img src="/newLogo.svg" alt="IELTS Logo" height="32" />
      </Box>

      {/* Center: Timer and optional middle content */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {timerText && (
          <TimerContainer>
            <TimerIcon />
            <Typography variant="body1" component="span">
              {renderTimerText(timerText)}
            </Typography>
          </TimerContainer>
        )}
        {middleContent}
      </Box>

      {/* Right: Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showReview && (
          <ReviewButton 
            startIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>📋</Box>}
            onClick={onReview}
          >
            Review
          </ReviewButton>
        )}

        {/* Exit button */}
        {/* {showSaveExit && !apiResult && (
          <Button
            onClick={onSaveExit}
            sx={{
              backgroundColor: '#FFA726',
              color: 'white',
              borderRadius: '8px',
              padding: '6px 16px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#FF9800',
              },
            }}
            startIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>🚪</Box>}
          >
            Exit
          </Button>
        )} */}

        {/* Submit/Results button */}
        {showSubmit && (
          <SubmitButton 
            onClick={handleSubmitClick}
            disabled={submitButtonDisabled || isSubmitting}
            endIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>➔</Box>}
          >
            {getSubmitButtonText()}
          </SubmitButton>
        )}
      </Box>
    </Header>
  );
};

export default IELTSHeader;

